import { FormEvent, useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import trim from 'lodash/trim';
import { useSearchParams } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import {
    loadCpaProductsAsync,
    loadCpaProductsRecomendationsAsync,
    loadCpaProductsSuggestAsync,
} from '../../../../data-layer/cpa/actions';
import {
    selectCpaProducts,
    selectCpaProductsLoading,
    selectCpaProductsRecomendations,
    selectCpaProductsRecomendationsLoading,
    selectCpaProductsSuggest,
} from '../../../../data-layer/cpa/selectors';
import { useDispatch } from '../../../../data-layer/useDispatch';
import { useSelector } from '../../../../data-layer/useSelector';
import { TIMEOUT } from '../constants';

export const useCpaProducts = () => {
    const dispatch = useDispatch();

    const recomendations = useSelector(selectCpaProductsRecomendations);
    const isLoadingRecomendations = useSelector(selectCpaProductsRecomendationsLoading);

    const products = useSelector(selectCpaProducts);
    const isLoadingProducts = useSelector(selectCpaProductsLoading);

    const loadProducts = useMemo(
        () =>
            debounce((queryText: string) => {
                dispatch(loadCpaProductsAsync(queryText));
            }, TIMEOUT),
        [dispatch],
    );

    const productsSuggest = useSelector(selectCpaProductsSuggest);
    const loadProductsSuggest = useMemo(
        () =>
            debounce((queryTtext: string) => {
                dispatch(loadCpaProductsSuggestAsync(queryTtext));
            }, TIMEOUT),
        [dispatch],
    );

    const [query, setQuery] = useSearchParams();
    const text = query.get('text') ?? '';
    const queryText = trim(text);
    const [isSearchMode, setIsSearchMode] = useState(Boolean(queryText));

    const onChangeText = useCallback(
        (event: FormEvent<HTMLInputElement>) => {
            const textValue = event.currentTarget.value;
            const trimTextValue = trim(textValue);

            setQuery({ text: textValue }, { replace: true });

            trimTextValue && loadProductsSuggest(trimTextValue);
        },
        [loadProductsSuggest, setQuery],
    );

    const onApplySuggest = useCallback(
        (textValue: string = '') => {
            setQuery({ text: textValue }, { replace: true });

            const trimTextValue = trim(textValue);

            trimTextValue && loadProducts(trimTextValue);

            setIsSearchMode(Boolean(trimTextValue));
        },
        [loadProducts, setQuery],
    );

    useEffectOnce(() => {
        if (!queryText) {
            dispatch(loadCpaProductsRecomendationsAsync());

            return;
        }

        loadProductsSuggest(queryText);
        loadProducts(queryText);
    });

    return {
        recomendations,
        isLoadingRecomendations,
        products,
        isLoadingProducts,
        productsSuggest,
        text,
        isSearchMode,
        onChangeText,
        onApplySuggest,
    };
};
